const SELECTORS = {
  mount: '[data-shc-mount]',
};

const HYVOR_EMBED_SRC = 'https://talk.hyvor.com/embed/embed.js';

let embedScriptPromise = null;

function loadEmbedScript() {
  if (embedScriptPromise) return embedScriptPromise;
  if (customElements.get('hyvor-talk-comments')) {
    embedScriptPromise = Promise.resolve();
    return embedScriptPromise;
  }

  embedScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${HYVOR_EMBED_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('[HyvorTalkComments] Embed script failed to load')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = HYVOR_EMBED_SRC;
    script.type = 'module';
    script.async = true;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('[HyvorTalkComments] Embed script failed to load')), { once: true });
    document.head.appendChild(script);
  });

  return embedScriptPromise;
}

class HyvorTalkComments extends HTMLElement {
  #observer = null;
  #embedded = false;

  connectedCallback() {
    try {
      this.#init();
    } catch (error) {
      console.error('[HyvorTalkComments:init]', error);
    }
  }

  #init() {
    this.#getDataAttributes();
    this.#validateDataAttributes();
    this.mount = this.querySelector(SELECTORS.mount);
    if (!this.mount) {
      throw new Error('[HyvorTalkComments] Mount element not found');
    }

    if (this.lazy && 'IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              this.#observer.disconnect();
              this.#observer = null;
              void this.#embed().catch((error) =>
                console.error('[HyvorTalkComments:embed]', error)
              );
              break;
            }
          }
        },
        { rootMargin: '200px 0px' }
      );
      this.#observer.observe(this);
    } else {
      void this.#embed().catch((error) =>
        console.error('[HyvorTalkComments:embed]', error)
      );
    }
  }

  #getDataAttributes() {
    this.websiteId = Number(this.dataset.shcWebsiteId);
    this.pageId = this.dataset.shcPageId || '';
    this.pageUrl = this.dataset.shcPageUrl || '';
    this.pageTitle = this.dataset.shcPageTitle || '';
    this.lang = this.dataset.shcLang || '';
    this.colorScheme = this.dataset.shcColorScheme || '';
    this.lazy = this.dataset.shcLazy !== 'false';
  }

  #validateDataAttributes() {
    if (!Number.isInteger(this.websiteId) || this.websiteId <= 0) {
      throw new Error(`[HyvorTalkComments] Invalid website ID: ${this.dataset.shcWebsiteId}`);
    }
    if (!this.pageId) {
      throw new Error('[HyvorTalkComments] Missing page ID');
    }
  }

  async #embed() {
    if (this.#embedded) return;
    this.#embedded = true;

    await loadEmbedScript();

    const comments = document.createElement('hyvor-talk-comments');
    comments.setAttribute('website-id', String(this.websiteId));
    comments.setAttribute('page-id', this.pageId);
    if (this.pageUrl) comments.setAttribute('page-url', this.pageUrl);
    if (this.pageTitle) comments.setAttribute('page-title', this.pageTitle);
    if (this.lang) comments.setAttribute('language', this.lang);
    if (this.colorScheme) comments.setAttribute('color-scheme', this.colorScheme);

    this.mount.replaceChildren(comments);
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  #cleanup() {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
  }
}

if (!customElements.get('sewsimple-hyvor-comments')) {
  customElements.define('sewsimple-hyvor-comments', HyvorTalkComments);
}
